import dayjs from 'dayjs';

/**
 * ════════════════ LA REGOLA DELLE DATE ════════════════
 *
 * **Una data vive come stringa. `Date` esiste solo dentro un calcolo, e non esce da quella
 * funzione.**
 *
 * Non e' una preferenza di stile: e' la forma che usa gia' tutto il resto del codice. Il BE
 * manda stringhe, `formatDate`/`formatDateTime` qui sotto prendono stringhe, e il builder
 * JSON:API **non ha nessuna gestione di `Date`** — un `Date` che arrivasse a un attributo lo
 * serializzerebbe `JSON.stringify` come istante UTC, cioe' il giorno prima per chiunque stia a
 * est di Greenwich. Fino al 17-09-2026 il `Date` esisteva solo perche' i date picker di Mantine
 * 7 lo pretendevano: dalla 9 danno e prendono stringhe, e quell'isola non ha piu' ragione.
 *
 * Formati, e sono due diversi — la differenza conta:
 *   - data sola      `YYYY-MM-DD`            (`DatePickerInput`, `DateInput`, `DatePicker`)
 *   - data e ora     `YYYY-MM-DD HH:mm:ss`   (`DateTimePicker`) ⚠️ con lo **spazio**, non la `T`
 *
 * ⚠️ Lo spazio e' la trappola: un ISO per il BE vuole la `T`. Chi compone a mano una stringa da
 * mandare parta da `fromDateTimePicker`, che la converte, invece di concatenare il valore grezzo.
 *
 * Il confronto fra due `YYYY-MM-DD` e' lessicografico ed **e' corretto**: `'2026-09-17' <
 * '2026-10-01'` funziona, non serve convertire per ordinare o confrontare. Per l'aritmetica vera
 * (aggiungi giorni, inizio mese) si usa dayjs, che accetta la stringa e rida' la stringa.
 */

/**
 * Oggi come `YYYY-MM-DD`, letto dall'orologio **locale**.
 *
 * ⚠️ Non e' `new Date().toISOString().slice(0, 10)`: quello e' oggi **a Greenwich**, e in Italia
 * dopo le 22 (d'estate le 23) restituisce il giorno prima. Il codice ne ha diverse occorrenze
 * ereditate; le nuove passano da qui.
 */
export function oggiIso(): string {
  return dayjs().format('YYYY-MM-DD');
}

/**
 * Valore per un `DateTimePicker`: `YYYY-MM-DD HH:mm:ss`, con lo spazio che vuole Mantine.
 * Accetta quel che c'e' in giro — una stringa del BE, un `Date` ereditato, niente.
 */
export function toDateTimePicker(v?: string | Date | null): string | null {
  if (!v) return null;
  const d = dayjs(typeof v === 'string' ? normalizeIsoUtc(v) : v);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : null;
}

/**
 * Dal valore di un `DateTimePicker` all'ISO che il BE si aspetta: `YYYY-MM-DDTHH:mm:ss`.
 * E' solo lo spazio che diventa `T`, ma dimenticarlo manda al server una stringa che qualche
 * parser accetta e qualche altro rifiuta — e non si scopre scrivendo, si scopre salvando.
 */
export function fromDateTimePicker(v?: string | null): string | null {
  if (!v) return null;
  return v.includes('T') ? v : v.replace(' ', 'T');
}


/**
 * Giorno di calendario `YYYY-MM-DD` da mandare al BE sui campi **date-only**.
 *
 * Dal contratto del 27-08-2026 (`BE-CHANGELOG-FOR-REACT-CONTRACT-CONFORMANCE-v1`) dodici campi
 * — le date di `invoice`, `invoice-installment`, `invoice-line` e quelle dei turni — smettono di
 * essere istanti e diventano giorni. In scrittura una stringa con l'orario non viene più troncata
 * in silenzio: risponde **422**.
 *
 * Il punto non è la severità nuova, è il bug che nascondeva. `toISOString()` su un `Date`
 * costruito da componenti locali sposta il giorno: `new Date(2026, 6, 2)` in `UTC+2` vale
 * `2026-07-01T22:00:00Z`, e serializzato diventa **1 luglio**. Su una data qualsiasi è un
 * fastidio; su `invoice.invoiceDate`, che è una data fiscale con numero progressivo, è un
 * documento sbagliato. Qui si formatta sempre dai componenti *locali*, mai via UTC.
 *
 * Una stringa già in forma `YYYY-MM-DD…` viene troncata senza riparsare: reinterpretarla
 * significherebbe applicarle un fuso che non ha.
 */
export function toDateOnly(v?: string | Date | null): string | null {
  if (!v) return null;
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
    const d = dayjs(v);
    return d.isValid() ? d.format('YYYY-MM-DD') : null;
  }
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : null;
}

/**
 * Normalizza una stringa data/ora ISO dal BE: se contiene la 'T' ma manca l'indicazione
 * del fuso (Z o +/-), aggiunge 'Z' per forzare il parsing come UTC invece di ora locale.
 */
export function normalizeIsoUtc(v: string): string {
  const str = v.trim();
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str) && !/[Zz]|[+-]\d{2}:?\d{2}$/.test(str)) {
    return `${str}Z`;
  }
  return str;
}

/** Un istante del server (UTC, spesso senza «Z») come dayjs locale. */
export function serverInstant(v: string): dayjs.Dayjs {
  return dayjs(normalizeIsoUtc(v));
}

/** Data in formato italiano (default DD/MM/YYYY). Valore vuoto/non valido → '—'. */
export function formatDate(v?: string | null, fmt = 'DD/MM/YYYY'): string {
  if (!v) return '—';
  const d = dayjs(normalizeIsoUtc(v));
  return d.isValid() ? d.format(fmt) : '—';
}

/** Data e ora in formato italiano (default DD/MM/YYYY HH:mm). Vuoto/non valido → '—'. */
export function formatDateTime(v?: string | null, fmt = 'DD/MM/YYYY HH:mm'): string {
  if (!v) return '—';
  const d = dayjs(normalizeIsoUtc(v));
  return d.isValid() ? d.format(fmt) : '—';
}

/** Importo in valuta stile IT: 1.234,56 €. Null/undefined → '—'. */
export function formatMoney(v?: number | null, currency = 'EUR'): string {
  if (v == null || Number.isNaN(v)) return '—';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(v);
}

/** Numero stile IT con separatore delle migliaia (default 0 decimali). Null → '—'. */
export function formatNumber(v?: number | null, decimals = 0): string {
  if (v == null || Number.isNaN(v)) return '—';
  return new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(v);
}
